import React, { useEffect, useState } from 'react';
import InternalAdCanvasView, {
  type InternalCreativeState,
  type InternalMediaState,
} from '../components/admin/InternalAdCanvasView';
import { lookalInternalAdService, isUploadedPosterAd, type LookalInternalAd } from '../services/lookalInternalAdService';

type PreviewWindow = Window & {
  __previewReady?: boolean;
  __previewError?: string;
};

const DEFAULT_CREATIVE: InternalCreativeState = {
  selectedFont: 'Inter, sans-serif',
  headlineFontSize: 44,
  benefitFontSize: 24,
  descriptionFontSize: 22,
  ctaFontSize: 22,
  headlineColor: '#0066FF',
  benefitColor: '#111827',
  descriptionColor: '#374151',
  ctaColor: '#FFFFFF',
  headlineAlign: 'left',
  benefitAlign: 'left',
  descriptionAlign: 'left',
  ctaAlign: 'center',
  headlineBold: true,
  benefitBold: true,
  descriptionBold: false,
  ctaBold: true,
  headlineItalic: false,
  benefitItalic: false,
  descriptionItalic: false,
  ctaItalic: false,
  headlineShadow: false,
  benefitShadow: false,
  descriptionShadow: false,
  ctaShadow: false,
  headlineX: 50,
  headlineY: 50,
  benefitX: 50,
  benefitY: 50,
  descriptionX: 50,
  descriptionY: 50,
  ctaX: 50,
  ctaY: 50,
  specialEffect: 'none',
  blurIntensity: 4,
  autoEnhance: true,
  useBackgroundOnly: true,
  overlayPosition: { x: 50, y: 50 },
  overlaySize: 35,
  logoPosition: { x: 82, y: 12 },
  logoSize: 18,
};

const getRecord = (ad: LookalInternalAd): Record<string, unknown> => ad.ad_content ?? {};

const getString = (record: Record<string, unknown>, key: string, fallback: string): string =>
  typeof record[key] === 'string' && record[key] ? record[key] as string : fallback;

const getNumber = (record: Record<string, unknown>, key: string, fallback: number): number =>
  typeof record[key] === 'number' && Number.isFinite(record[key]) ? record[key] as number : fallback;

const getBoolean = (record: Record<string, unknown>, key: string, fallback: boolean): boolean => {
  const value = record[key];
  return typeof value === 'boolean' ? value : fallback;
};

const getAlign = (record: Record<string, unknown>, key: string, fallback: InternalCreativeState['headlineAlign']): InternalCreativeState['headlineAlign'] => {
  const value = record[key];
  return value === 'left' || value === 'center' || value === 'right' ? value : fallback;
};

const getPosition = (record: Record<string, unknown>, key: string, fallback: { x: number; y: number }) => {
  const value = record[key];
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return fallback;
  const position = value as Record<string, unknown>;
  return {
    x: getNumber(position, 'x', fallback.x),
    y: getNumber(position, 'y', fallback.y),
  };
};

const buildCreative = (ad: LookalInternalAd): InternalCreativeState => {
  const content = getRecord(ad);
  return {
    ...DEFAULT_CREATIVE,
    selectedFont: getString(content, 'selectedFont', DEFAULT_CREATIVE.selectedFont),
    headlineFontSize: getNumber(content, 'headlineFontSize', DEFAULT_CREATIVE.headlineFontSize),
    benefitFontSize: getNumber(content, 'benefitFontSize', DEFAULT_CREATIVE.benefitFontSize),
    descriptionFontSize: getNumber(content, 'descriptionFontSize', DEFAULT_CREATIVE.descriptionFontSize),
    ctaFontSize: getNumber(content, 'ctaFontSize', DEFAULT_CREATIVE.ctaFontSize),
    headlineColor: getString(content, 'headlineColor', DEFAULT_CREATIVE.headlineColor),
    benefitColor: getString(content, 'benefitColor', DEFAULT_CREATIVE.benefitColor),
    descriptionColor: getString(content, 'descriptionColor', DEFAULT_CREATIVE.descriptionColor),
    ctaColor: getString(content, 'ctaColor', DEFAULT_CREATIVE.ctaColor),
    headlineAlign: getAlign(content, 'headlineAlign', DEFAULT_CREATIVE.headlineAlign),
    benefitAlign: getAlign(content, 'benefitAlign', DEFAULT_CREATIVE.benefitAlign),
    descriptionAlign: getAlign(content, 'descriptionAlign', DEFAULT_CREATIVE.descriptionAlign),
    ctaAlign: getAlign(content, 'ctaAlign', DEFAULT_CREATIVE.ctaAlign),
    headlineBold: getBoolean(content, 'headlineBold', DEFAULT_CREATIVE.headlineBold),
    benefitBold: getBoolean(content, 'benefitBold', DEFAULT_CREATIVE.benefitBold),
    descriptionBold: getBoolean(content, 'descriptionBold', DEFAULT_CREATIVE.descriptionBold),
    ctaBold: getBoolean(content, 'ctaBold', DEFAULT_CREATIVE.ctaBold),
    headlineItalic: getBoolean(content, 'headlineItalic', DEFAULT_CREATIVE.headlineItalic),
    benefitItalic: getBoolean(content, 'benefitItalic', DEFAULT_CREATIVE.benefitItalic),
    descriptionItalic: getBoolean(content, 'descriptionItalic', DEFAULT_CREATIVE.descriptionItalic),
    ctaItalic: getBoolean(content, 'ctaItalic', DEFAULT_CREATIVE.ctaItalic),
    headlineShadow: getBoolean(content, 'headlineShadow', DEFAULT_CREATIVE.headlineShadow),
    benefitShadow: getBoolean(content, 'benefitShadow', DEFAULT_CREATIVE.benefitShadow),
    descriptionShadow: getBoolean(content, 'descriptionShadow', DEFAULT_CREATIVE.descriptionShadow),
    ctaShadow: getBoolean(content, 'ctaShadow', DEFAULT_CREATIVE.ctaShadow),
    headlineX: getNumber(content, 'headlineX', DEFAULT_CREATIVE.headlineX),
    headlineY: getNumber(content, 'headlineY', DEFAULT_CREATIVE.headlineY),
    benefitX: getNumber(content, 'benefitX', DEFAULT_CREATIVE.benefitX),
    benefitY: getNumber(content, 'benefitY', DEFAULT_CREATIVE.benefitY),
    descriptionX: getNumber(content, 'descriptionX', DEFAULT_CREATIVE.descriptionX),
    descriptionY: getNumber(content, 'descriptionY', DEFAULT_CREATIVE.descriptionY),
    ctaX: getNumber(content, 'ctaX', DEFAULT_CREATIVE.ctaX),
    ctaY: getNumber(content, 'ctaY', DEFAULT_CREATIVE.ctaY),
    specialEffect: content.specialEffect === 'blur' || content.specialEffect === 'gradient' || content.specialEffect === 'shadow'
      ? content.specialEffect
      : DEFAULT_CREATIVE.specialEffect,
    blurIntensity: getNumber(content, 'blurIntensity', DEFAULT_CREATIVE.blurIntensity),
    autoEnhance: getBoolean(content, 'autoEnhance', DEFAULT_CREATIVE.autoEnhance),
    useBackgroundOnly: getBoolean(content, 'useBackgroundOnly', DEFAULT_CREATIVE.useBackgroundOnly),
    overlayPosition: getPosition(content, 'overlayPosition', DEFAULT_CREATIVE.overlayPosition),
    overlaySize: getNumber(content, 'overlaySize', DEFAULT_CREATIVE.overlaySize),
    logoPosition: getPosition(content, 'logoPosition', DEFAULT_CREATIVE.logoPosition),
    logoSize: getNumber(content, 'logoSize', DEFAULT_CREATIVE.logoSize),
  };
};

const buildMedia = (ad: LookalInternalAd): InternalMediaState => {
  const content = getRecord(ad);
  return {
    adImageUrl: getString(content, 'adImageUrl', '') || null,
    overlayImageUrl: getString(content, 'overlayImageUrl', '') || null,
    logoUrl: getString(content, 'logoUrl', '') || null,
  };
};

const markPreviewError = (message: string) => {
  const previewWindow = window as PreviewWindow;
  previewWindow.__previewReady = false;
  previewWindow.__previewError = message;
};

const PublicInternalAdPreview = () => {
  const [ad, setAd] = useState<LookalInternalAd | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const previewWindow = window as PreviewWindow;
    previewWindow.__previewReady = false;
    delete previewWindow.__previewError;

    const load = async () => {
      const adId = new URLSearchParams(window.location.search).get('adId');
      const result = adId
        ? await lookalInternalAdService.getById(adId)
        : await lookalInternalAdService.getActive();
      if (cancelled) return;
      if (!result.success || !result.data) {
        const message = result.error ?? 'No active internal ad is available.';
        setError(message);
        markPreviewError(message);
        return;
      }
      setAd(result.data);
    };

    void load().catch(loadError => {
      if (cancelled) return;
      const message = loadError instanceof Error ? loadError.message : 'Failed to load internal ad preview.';
      setError(message);
      markPreviewError(message);
    });

    return () => {
      cancelled = true;
      previewWindow.__previewReady = false;
    };
  }, []);

  useEffect(() => {
    if (!ad) return;
    let cancelled = false;
    const markReady = () => {
      if (!cancelled) {
        (window as PreviewWindow).__previewReady = true;
      }
    };
    const timeoutId = window.setTimeout(markReady, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [ad]);

  if (error) {
    return <main data-internal-ad-preview="true">{error}</main>;
  }

  if (!ad) {
    return <main data-internal-ad-preview="true">Loading internal ad preview...</main>;
  }

  const content = getRecord(ad);
  const creative = buildCreative(ad);
  const media = buildMedia(ad);

  // Own-content ads publish the operator's uploaded artwork as Zone A. Rendering
  // the canvas here instead would screenshot a design they never used.
  const isOwnPoster = isUploadedPosterAd(ad);
  const posterUrl = ad.preview_url ?? ad.image_url;

  return (
    <main
      data-internal-ad-preview="true"
      className="flex h-[720px] w-[640px] items-center justify-center overflow-hidden bg-white"
    >
      {isOwnPoster && posterUrl ? (
        // Keep the `data-ad-canvas` contract: the screenshot worker locates this
        // element and captures it. Rendering a bare <img> made every own-content
        // job fail with "Ad canvas element not found".
        <div
          id="final-ad-canvas"
          data-ad-canvas="true"
          className="h-[720px] w-[640px] overflow-hidden bg-white"
        >
          <img
            src={posterUrl}
            alt={ad.name || 'Internal ad poster'}
            className="h-full w-full object-cover"
            crossOrigin="anonymous"
          />
        </div>
      ) : (
        <InternalAdCanvasView
          headline={getString(content, 'headline', '')}
          benefit={getString(content, 'benefit', '')}
          description={getString(content, 'description', '')}
          callToAction={ad.cta ?? getString(content, 'cta', '')}
          designFramework={getString(content, 'designFramework', 'Vertical Flow')}
          primaryColor={getString(content, 'primaryColor', '#0066FF')}
          secondaryColor={getString(content, 'secondaryColor', '#000000')}
          accentColor={getString(content, 'accentColor', '#3B82F6')}
          creative={creative}
          media={media}
        />
      )}
    </main>
  );
};

export default PublicInternalAdPreview;
